import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useDataLayerValue } from "../../DataLayer/DataLayer";
import {
  createSeoMetaTag,
  updateSeoMetaTagData,
  checkSeoMetaTagSlug,
} from "../../utils/ApiCalls/SeoMetaTag";
import { addlog } from "../../utils/ApiCalls/Logs";
import { LOG_ACTIONS } from "../../utils/Constant/LogConstant";

const LANGUAGE_FIELDS = [
  { key: "", label: "English" },
  { key: "simplified_chinese_", label: "Simplified Chinese" },
  { key: "traditional_chinese_", label: "Traditional Chinese" },
  { key: "bahasa_", label: "Bahasa" },
];

const SeoMetaTagInput = ({ seoMetaTag = {} }) => {
  const navigate = useNavigate();
  const [{ userDetails }, dispatch] = useDataLayerValue();

  const [pageSlug, setPageSlug] = useState("");
  const [fields, setFields] = useState({});
  const [slugError, setSlugError] = useState("");
  const [checkingSlug, setCheckingSlug] = useState(false);
  const slugCheckToken = useRef(0);

  useEffect(() => {
    if (seoMetaTag?.id) {
      setPageSlug(seoMetaTag.page_slug || "");
      const initial = {};
      LANGUAGE_FIELDS.forEach(({ key }) => {
        initial[`${key}title`] = seoMetaTag[`${key}title`] || "";
        initial[`${key}description`] = seoMetaTag[`${key}description`] || "";
      });
      setFields(initial);
    }
  }, [seoMetaTag]);

  const handleFieldChange = (name, value) => {
    setFields((prev) => ({ ...prev, [name]: value }));
  };

  const validateSlug = async (slug) => {
    if (!slug) {
      setSlugError("Page slug is required");
      return false;
    }
    const token = ++slugCheckToken.current;
    setCheckingSlug(true);
    const result = await checkSeoMetaTagSlug({ slug, id: seoMetaTag?.id });
    if (token !== slugCheckToken.current) return true;
    setCheckingSlug(false);
    if (result?.error) return true;
    if (result?.exists) {
      setSlugError("Slug is already present");
      return false;
    }
    setSlugError("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!pageSlug.trim()) {
      setSlugError("Page slug is required");
      return;
    }

    const slugOk = await validateSlug(pageSlug.trim());
    if (!slugOk) {
      return Swal.fire({ icon: "error", title: "Oops...", text: "Slug is already present" });
    }

    dispatch({ type: "SET_LOADING", loading: true });
    try {
      const payload = { ...fields, page_slug: pageSlug.trim() };
      const data = seoMetaTag?.id
        ? await updateSeoMetaTagData(seoMetaTag.id, payload)
        : await createSeoMetaTag(payload);

      if (data?.error) {
        if (/slug already exists/i.test(data.error)) {
          setSlugError("Slug is already present");
          return Swal.fire({ icon: "error", title: "Oops...", text: "Slug is already present" });
        }
        return Swal.fire({ icon: "error", title: "Oops...", text: data.error });
      }

      addlog(
        seoMetaTag?.id ? LOG_ACTIONS.UPDATED : LOG_ACTIONS.CREATED,
        "SEO Meta Tag",
        { id: data?.id || seoMetaTag?.id },
        userDetails.id
      );

      await Swal.fire({
        icon: "success",
        title: seoMetaTag?.id ? "SEO Meta Tag updated" : "SEO Meta Tag added",
        timer: 1500,
        showConfirmButton: false,
      });
      navigate("/website/webpages/seo-meta-tag");
    } finally {
      dispatch({ type: "SET_LOADING", loading: false });
    }
  };

  return (
    <div>
      <section className="content">
        <div className="container-fluid">
          <div className="card">
            <form className="input_form" onSubmit={handleSubmit}>
              <div className="card-body">
                <div className="row">
                  <div className="col-lg-6 col-md-6 col-12">
                    <div className="fieldrow">
                      <label className="f_label">
                        Page Slug <span style={{ color: "red" }}>*</span>
                      </label>
                      <input
                        className="f_input"
                        type="text"
                        placeholder="e.g. home, about-us"
                        value={pageSlug}
                        onChange={(e) => {
                          setPageSlug(e.target.value);
                          setSlugError("");
                        }}
                        onBlur={(e) => validateSlug(e.target.value.trim())}
                      />
                      {checkingSlug && (
                        <span style={{ fontSize: "12px" }}>Checking slug…</span>
                      )}
                      {slugError && (
                        <span style={{ color: "red", fontSize: "12px" }}>{slugError}</span>
                      )}
                    </div>
                  </div>

                  {LANGUAGE_FIELDS.map(({ key, label }) => (
                    <div className="col-lg-12" key={key || "en"}>
                      <hr />
                      <h6>{label} SEO</h6>
                      <div className="row">
                        <div className="col-lg-6 col-md-6 col-12">
                          <div className="fieldrow">
                            <label className="f_label">{label} Meta Title</label>
                            <input
                              className="f_input"
                              type="text"
                              placeholder={`${label} meta title`}
                              value={fields[`${key}title`] || ""}
                              onChange={(e) => handleFieldChange(`${key}title`, e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="col-lg-6 col-md-6 col-12">
                          <div className="fieldrow">
                            <label className="f_label">{label} Meta Description</label>
                            <textarea
                              className="f_input"
                              rows={3}
                              placeholder={`${label} meta description`}
                              value={fields[`${key}description`] || ""}
                              onChange={(e) => handleFieldChange(`${key}description`, e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="card-footer text-right">
                <button className="btn main_submit">
                  {seoMetaTag?.id ? "Update" : "Add"} SEO Meta Tag
                </button>
              </div>
            </form>
          </div>
          <div className="pt-3"></div>
        </div>
      </section>
    </div>
  );
};

export default SeoMetaTagInput;
